import { FileModel } from '../../../../typings/api/skymap/rest/v1/.common';
import { findLastFileVersion } from '../../utils/models/file-version';
import { isDefined, isIncluded } from '../../utils/variables';
import { getFileExtension } from './path';

const extensionToMimeType: Record<string, string> = {
  '.gif': 'image/gif',
  '.jpeg': 'image/jpeg',
  '.jpg': 'image/jpeg',
  '.pdf': 'application/pdf',
  '.png': 'image/png',
  '.txt': 'text/plain',
  '.webp ': 'image/webp',
};

const fmeCompatibleFormats = ['.dwg', '.dxf'] as const;

const glbCompatibleFormats = [
  '.geo',
  '.glb',
  '.ifc',
  '.kml',
  '.kmz',
  '.shp',
  '.shape',
  '.json',
  '.geojson',
  '.jpeg',
  '.jpg',
  '.pdf',
  '.png',
  '.xml',
  '.dae',
  '.zip',
] as const;

export const imageCompatibleFormat = ['.jpg', '.jpeg', '.png', '.webp'] as const;

export const validSkyViewFileExtensions = Array.from(
  new Set([...fmeCompatibleFormats, ...glbCompatibleFormats, ...imageCompatibleFormat]),
) as string[];

export type FileManagerCadFileStatus =
  | 'COMPLETED'
  | 'FAILED'
  | 'RUNNING'
  | 'NOT_LAND_XML'
  | 'RUNNABLE'
  | 'UNKNOWN';

/**
 * Returns next revision if revision is specified, otherwise undefined.
 * todo: Copied from old document manager. Consider changing logic to accept all valid characters
 * todo: for revision.
 * @param revision Revision of latest existing file version.
 */
export const getNextFileVersionRevision = (revision?: string) => {
  const revisionLetters = 'ABCDEFGHIJKLMNOPQRSTUVXYZ';

  if (!revision) {
    return undefined;
  }

  const lastRevisionLetter = revision.toUpperCase()[revision.length - 1];
  const letterIndex = revisionLetters.indexOf(lastRevisionLetter);
  let nextRevision = revision.toUpperCase();
  if (letterIndex === -1 || letterIndex === revisionLetters.length - 1) {
    let i = revision.length - 1;
    for (; i >= 0 && nextRevision[i] === 'Z'; i--) {
      nextRevision = nextRevision.substr(0, i) + 'A' + nextRevision.substr(i + 1);
    }
    if (i > -1) {
      const curRevisionLetter = nextRevision.toUpperCase()[i];
      const curletterIndex = revisionLetters.indexOf(curRevisionLetter);
      nextRevision =
        nextRevision.substr(0, i) +
        revisionLetters.charAt(curletterIndex + 1) +
        nextRevision.substr(i + 1);
    } else {
      nextRevision = nextRevision + 'A';
    }
  } else {
    nextRevision = nextRevision.slice(0, -1) + revisionLetters.charAt(letterIndex + 1);
  }

  return nextRevision;
};

export const canFileBeOpenedInline = (fileName: string) => {
  const contentType = extensionToMimeType[getFileExtension(fileName).toLowerCase()];
  return contentType !== undefined;
};

/**
 * Returns a value indicating whether the last file version in the specified file, contains either
 * a DXF or GLB object. This means the file might be added to SkyView depending on the
 * status.
 */
export const lastFileVersionHasDxfOrGlb = (file: FileModel) => {
  if (getFileExtension(file.name) === '.pdf') {
    return true;
  }

  const lastVersion = findLastFileVersion(file);
  return isDefined(lastVersion.dxf) || isDefined(lastVersion.glb);
};

/**
 * Returns a value indicating whether the last file version in the specified file, contains either
 * a DXF or GLB object with a successful status.
 */
export const isLastFileVersionReadyForSkyView = (file: FileModel) => {
  // PDF files are processed for skyview upon adding them, so they are always considered ready.
  if (getFileExtension(file.name) === '.pdf') {
    return 'COMPLETED';
  }

  const lastFileVersion = findLastFileVersion(file);

  const status = lastFileVersion.dxf
    ? resolveLastFileVersionCadStatus(file)
    : lastFileVersion.glb
      ? resolveLastFileVersionCadStatus(file)
      : undefined;

  return status === 'COMPLETED';
};

/**
 * Returns a less fine grained status for the specified cad status (groups similar statuses
 * from .dxf, and .glb toghether respectively).
 */
export const resolveLastFileVersionCadStatus = (file: FileModel): FileManagerCadFileStatus => {
  const lastFileVersion = findLastFileVersion(file);
  const extension = getFileExtension(file.name);

  if (extension === '.pdf') {
    return 'COMPLETED';
  }

  if (lastFileVersion.dxf) {
    switch (lastFileVersion.dxf.status) {
      case 'COMPLETED':
        return 'COMPLETED';
      case 'FAILED':
        return 'FAILED';
      case 'RUNNING':
        return 'RUNNING';
      default:
        return 'UNKNOWN';
    }
  }

  if (lastFileVersion.glb) {
    switch (lastFileVersion.glb.status) {
      case 'RUNNING':
        return 'RUNNING';
      case 'COMPLETED':
        return 'COMPLETED';
      case 'NOT_LAND_XML':
        return 'NOT_LAND_XML';
      case 'GEOMETRY_INVALID':
      case 'GEOMETRY_MISSING':
      case 'UNKNOWN_ERROR':
      case 'VERSION_UNSUPPORTED':
        return 'FAILED';
      default:
        return 'UNKNOWN';
    }
  }

  if (isIncluded(glbCompatibleFormats, extension)) {
    return 'RUNNABLE';
  }

  if (isIncluded(fmeCompatibleFormats, extension)) {
    return 'FAILED';
  }

  return 'UNKNOWN';
};

const UNALLOWED_FILE_NAME_CHARS_REGEX = /[?\\/:"<>|]/g;

export const sanitizeFileBaseName = (fileName: string) =>
  fileName.replace(UNALLOWED_FILE_NAME_CHARS_REGEX, '_');
